import { getApiClient } from '../../api/client';
import type { components, operations } from '../../api/schema';
import { ExpectedApiError } from './errors';

export type BulkScorecardJob = components['schemas']['BulkScorecardJobRead'];
export type BulkScorecardJobPage = components['schemas']['PaginatedResponse_BulkScorecardJobRead_'];
type BulkScorecardUploadBody =
  components['schemas']['Body_create_bulk_scorecard_job_bulk_scorecard_jobs_post'];
type BulkScorecardListQuery = NonNullable<
  operations['list_bulk_scorecard_jobs_bulk_scorecard_jobs_get']['parameters']['query']
>;

function requestError(message: string, response?: Response): Error {
  return new Error(response?.status ? `${message} (${response.status})` : message);
}

function workbookError(error: unknown, response?: Response): Error {
  const status = response?.status ?? 0;
  if (
    status >= 400 &&
    status < 500 &&
    typeof error === 'object' &&
    error !== null &&
    'detail' in error &&
    typeof error.detail === 'string'
  ) {
    // Workbook validation (for example a missing header) is shown in the modal, not a crash.
    return new ExpectedApiError(error.detail);
  }
  return requestError('Could not upload the workbook', response);
}

export async function listBulkScorecardJobs(page = 1, limit = 25): Promise<BulkScorecardJobPage> {
  const query = { page, limit } satisfies BulkScorecardListQuery;
  const { data, error, response } = await getApiClient().GET('/bulk-scorecard-jobs', {
    params: { query },
  });
  if (error || !data) throw requestError('Could not load bulk jobs', response);
  return data;
}

export async function createBulkScorecardJob(file: File): Promise<BulkScorecardJob> {
  // OpenAPI models binary multipart fields as strings; openapi-fetch serializes this File correctly.
  const body = { file } as unknown as BulkScorecardUploadBody;
  let result;
  try {
    result = await getApiClient().POST('/bulk-scorecard-jobs', {
      body,
      bodySerializer: (value) => {
        const formData = new FormData();
        formData.append('file', (value as unknown as { file: File }).file);
        return formData;
      },
    });
  } catch (error) {
    // Chrome refuses to send a chosen file that was edited and saved since it was picked.
    if (error instanceof TypeError) {
      throw new ExpectedApiError(
        'The workbook could not be sent. If you edited the file after choosing it, choose it again.',
      );
    }
    throw error;
  }
  const { data, error, response } = result;
  if (error) throw workbookError(error, response);
  if (!data) throw requestError('Could not upload the workbook', response);
  return data;
}

export async function downloadBulkScorecardJob(jobUuid: string): Promise<Blob> {
  const { data, error, response } = await getApiClient().GET(
    '/bulk-scorecard-jobs/{job_uuid}/download',
    {
      params: { path: { job_uuid: jobUuid } },
      parseAs: 'blob',
    },
  );
  if (error || !(data instanceof Blob)) {
    throw requestError('Could not download the completed workbook', response);
  }
  return data;
}
